import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import {
  listFiles, presignFile, uploadFile, moveFile, deleteFile, tagFile, createFolder, removeFolder,
  type StoreObject, type StoreTags,
} from "../lib/brain-api";
import { Button, Card, EmptyState, Icon, Spinner } from "../design/ui";

/**
 * S3 content browser with a navigable folder structure. Folders are derived
 * from key segments under the entity prefix; empty folders persist via a
 * zero-byte `.keep` marker. Supports upload (into the open folder), move,
 * delete, create/remove folder, and attaching a file to a project (tag).
 */

const FB_CSS = `
.fb-crumbs { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; font-size: 13px; margin-bottom: 10px; }
.fb-crumb { background: none; border: none; padding: 2px 4px; cursor: pointer; color: var(--kt-teal-ink); font-family: var(--font-sans); font-size: 13px; border-radius: 4px; }
.fb-crumb:hover { background: var(--kt-strip-bg); }
.fb-crumb--current { color: var(--kt-ink); font-weight: 500; cursor: default; }
.fb-crumb--current:hover { background: none; }
.fb-sep { color: var(--kt-text-muted); }

.fb-row { display: flex; align-items: center; gap: 8px; padding: 7px 6px; border-bottom: 1px solid var(--kt-row-divider); border-radius: 4px; }
.fb-row:hover { background: var(--kt-strip-bg); }
.fb-row-name { background: none; border: none; cursor: pointer; text-align: left; display: flex; align-items: center; gap: 8px; min-width: 0; flex: 1; padding: 0; }
.fb-row-label { font-size: 13px; color: var(--kt-ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fb-row-label--mono { font-family: var(--font-mono); font-size: 12px; }
.fb-row-meta { font-size: 12px; color: var(--kt-text-muted); flex-shrink: 0; font-family: var(--font-mono); }
.fb-row-actions { display: flex; gap: 2px; flex-shrink: 0; opacity: 0; transition: opacity 0.1s ease; }
.fb-row:hover .fb-row-actions { opacity: 1; }
.fb-iconbtn { background: none; border: none; cursor: pointer; padding: 4px; border-radius: 4px; color: var(--kt-text-muted); display: inline-flex; }
.fb-iconbtn:hover { background: var(--kt-teal-tint); color: var(--kt-teal-ink); }
.fb-iconbtn--danger:hover { background: var(--kt-red-tint, var(--kt-strip-bg)); color: var(--kt-spine-red); }

.fb-chip { font-size: 10px; font-family: var(--font-mono); color: var(--kt-teal-ink); background: var(--kt-teal-tint); border-radius: 999px; padding: 1px 8px; flex-shrink: 0; }

.fb-drop { border: 2px dashed var(--kt-border); border-radius: 8px; padding: 12px; text-align: center; margin-top: 12px; transition: border-color 0.12s ease, background 0.12s ease; }
.fb-drop--over { border-color: var(--kt-teal); background: var(--kt-teal-tint); }
.fb-drop-hint { font-size: 12px; color: var(--kt-text-tertiary); }

.fb-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }

.fb-dialog { position: fixed; inset: 0; background: color-mix(in srgb, var(--kt-ink) 30%, transparent); display: flex; align-items: center; justify-content: center; z-index: 100; }
.fb-dialog-card { background: var(--kt-surface); border: 1px solid var(--kt-border); border-radius: 10px; padding: 18px; width: 380px; max-width: 90vw; display: flex; flex-direction: column; gap: 12px; }
.fb-dialog-title { font-size: 14px; font-weight: 600; color: var(--kt-ink-strong); }
.fb-dialog-select, .fb-dialog-input { font-family: var(--font-sans); font-size: 13px; padding: 7px 9px; border: 1px solid var(--kt-border); border-radius: 6px; background: var(--kt-surface); color: var(--kt-ink); width: 100%; }
.fb-dialog-actions { display: flex; justify-content: flex-end; gap: 8px; }
`;

interface Entry {
  kind: "folder" | "file";
  name: string;
  key: string;          // file: full key · folder: full prefix ending in /
  size?: number;
  lastModified?: string;
  project?: string;     // attached project tag (client-side echo only)
  isEmptyFolder?: boolean;
}

export function FileBrowser({ prefix, tags, projects }: {
  prefix: string;
  tags: StoreTags;
  /** Optional project ids offered by "Attach to project". */
  projects?: { id: string; name: string }[];
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [objects, setObjects] = useState<StoreObject[]>([]);
  const [path, setPath] = useState<string>("");     // folder path relative to prefix, "" or "a/b/"
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [moveTarget, setMoveTarget] = useState<Entry | null>(null);
  const [attachTarget, setAttachTarget] = useState<Entry | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setObjects(await listFiles(prefix)); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : "Failed to list files.", { variant: "error" }); }
    finally { setLoading(false); }
  }, [prefix, enqueueSnackbar]);
  useEffect(() => { void load(); }, [load]);

  // All folder prefixes (relative, ending in /) that exist anywhere under root —
  // derived from every key's directory segments, so nesting is navigable.
  const allFolders = useMemo(() => {
    const set = new Set<string>();
    for (const o of objects) {
      const rel = o.key.slice(prefix.length);
      const parts = rel.split("/").slice(0, -1);
      let acc = "";
      for (const p of parts) { acc += `${p}/`; set.add(acc); }
    }
    return [...set].sort();
  }, [objects, prefix]);

  // Entries at the currently open path: immediate subfolders + files.
  const entries = useMemo<Entry[]>(() => {
    const folders = new Map<string, { count: number }>();
    const files: Entry[] = [];
    for (const o of objects) {
      const rel = o.key.slice(prefix.length);
      if (!rel.startsWith(path)) continue;
      const rest = rel.slice(path.length);
      if (!rest) continue;
      const slash = rest.indexOf("/");
      if (slash >= 0) {
        const name = rest.slice(0, slash);
        const f = folders.get(name) ?? { count: 0 };
        if (!rest.endsWith("/.keep")) f.count++;
        folders.set(name, f);
      } else if (rest !== ".keep") {
        files.push({ kind: "file", name: rest, key: o.key, size: o.size, lastModified: o.lastModified });
      }
    }
    const folderEntries: Entry[] = [...folders.entries()].map(([name, f]) => ({
      kind: "folder", name, key: `${prefix}${path}${name}/`, isEmptyFolder: f.count === 0,
    }));
    return [
      ...folderEntries.sort((a, b) => a.name.localeCompare(b.name)),
      ...files.sort((a, b) => a.name.localeCompare(b.name)),
    ];
  }, [objects, prefix, path]);

  const run = async (fn: () => Promise<void>, ok: string) => {
    setBusy(true);
    try { await fn(); if (ok) enqueueSnackbar(ok, { variant: "success" }); await load(); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : "Operation failed.", { variant: "error" }); }
    finally { setBusy(false); }
  };

  const upload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void run(async () => {
      for (const file of Array.from(files)) await uploadFile(`${prefix}${path}${file.name}`, file, tags);
    }, `Uploaded ${files.length} file(s).`);
  };

  const open = async (key: string) => {
    try { window.open(await presignFile(key), "_blank", "noopener"); }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : "Could not open file.", { variant: "error" }); }
  };

  const newFolder = () => {
    const name = window.prompt("New folder name:");
    if (!name) return;
    const clean = name.trim().replace(/[\\/]+/g, "-");
    if (!clean) return;
    void run(() => createFolder(`${prefix}${path}${clean}/`, tags), `Folder "${clean}" created.`);
  };

  const delEntry = (e: Entry) => {
    if (e.kind === "folder") {
      if (!window.confirm(`Remove folder "${e.name}"? Only empty folders can be removed.`)) return;
      void run(() => removeFolder(e.key), "Folder removed.");
    } else {
      if (!window.confirm(`Delete "${e.name}"? The bucket keeps prior versions.`)) return;
      void run(() => deleteFile(e.key), "File deleted.");
    }
  };

  const doMove = (dest: string) => {
    const t = moveTarget;
    setMoveTarget(null);
    if (!t) return;
    const toKey = `${prefix}${dest}${t.name}`;
    if (toKey === t.key) return;
    void run(() => moveFile(t.key, toKey), `Moved to ${dest || "root"}.`);
  };

  const doAttach = (projectId: string) => {
    const t = attachTarget;
    setAttachTarget(null);
    if (!t || !projectId) return;
    void run(() => tagFile(t.key, { ...tags, project: projectId }), `Attached to ${projectId}.`);
  };

  const crumbs = ["", ...path.split("/").filter(Boolean).map((_, i, arr) => arr.slice(0, i + 1).join("/") + "/")];

  if (loading) return <Spinner label="Loading files…" />;

  return (
    <Card>
      <style>{FB_CSS}</style>

      <div className="fb-toolbar">
        <div className="fb-crumbs">
          {crumbs.map((p, i) => {
            const label = p === "" ? "Files" : p.split("/").filter(Boolean).pop();
            const current = p === path;
            return (
              <span key={p} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                {i > 0 && <span className="fb-sep">/</span>}
                <button className={`fb-crumb${current ? " fb-crumb--current" : ""}`} onClick={() => !current && setPath(p)}>{label}</button>
              </span>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Button icon="folder-plus" disabled={busy} onClick={newFolder}>New folder</Button>
          <input ref={inputRef} type="file" multiple style={{ display: "none" }} onChange={(e) => upload(e.target.files)} />
          <Button icon="upload" variant="primary" disabled={busy} onClick={() => inputRef.current?.click()}>Upload</Button>
        </div>
      </div>

      {entries.length === 0 ? (
        <EmptyState icon="folder" title="Empty folder">Drop a file below, or create a folder.</EmptyState>
      ) : (
        <div>
          {entries.map((e) => (
            <div key={e.key} className="fb-row">
              <button
                className="fb-row-name"
                onClick={() => (e.kind === "folder" ? setPath(e.key.slice(prefix.length)) : void open(e.key))}
              >
                <Icon name={e.kind === "folder" ? "folder" : "file"} size={15} color={e.kind === "folder" ? "var(--kt-teal)" : "var(--kt-text-muted)"} />
                <span className={`fb-row-label${e.kind === "file" ? " fb-row-label--mono" : ""}`}>{e.name}</span>
                {e.project && <span className="fb-chip">{e.project}</span>}
              </button>
              <span className="fb-row-meta">
                {e.kind === "file"
                  ? `${e.size != null ? `${Math.max(1, Math.round(e.size / 1024))} KB` : ""}${e.lastModified ? ` · ${new Date(e.lastModified).toLocaleDateString()}` : ""}`
                  : e.isEmptyFolder ? "empty" : ""}
              </span>
              <span className="fb-row-actions">
                {e.kind === "file" && (
                  <>
                    <button className="fb-iconbtn" title="Move to folder" onClick={() => setMoveTarget(e)}><Icon name="corner-down-right" size={14} /></button>
                    {projects && projects.length > 0 && (
                      <button className="fb-iconbtn" title="Attach to project" onClick={() => setAttachTarget(e)}><Icon name="box" size={14} /></button>
                    )}
                  </>
                )}
                {(e.kind === "file" || e.isEmptyFolder) && (
                  <button className="fb-iconbtn fb-iconbtn--danger" title="Delete" onClick={() => delEntry(e)}><Icon name="trash" size={14} /></button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      <div
        className={`fb-drop${dragOver ? " fb-drop--over" : ""}`}
        onDragOver={(ev) => { ev.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(ev) => { ev.preventDefault(); setDragOver(false); upload(ev.dataTransfer.files); }}
      >
        <span className="fb-drop-hint">
          Drop files to upload into <span style={{ fontFamily: "var(--font-mono)" }}>{path || "the root"}</span> — auto-tagged.
        </span>
      </div>

      {moveTarget && (
        <PickerDialog
          title={`Move "${moveTarget.name}" to…`}
          options={[{ value: "", label: "(root)" }, ...allFolders.map((f) => ({ value: f, label: f }))]}
          onCancel={() => setMoveTarget(null)}
          onPick={doMove}
        />
      )}
      {attachTarget && projects && (
        <PickerDialog
          title={`Attach "${attachTarget.name}" to project…`}
          options={projects.map((p) => ({ value: p.id, label: p.name }))}
          onCancel={() => setAttachTarget(null)}
          onPick={doAttach}
        />
      )}
    </Card>
  );
}

function PickerDialog({ title, options, onPick, onCancel }: {
  title: string;
  options: { value: string; label: string }[];
  onPick: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(options[0]?.value ?? "");
  return (
    <div className="fb-dialog" onClick={onCancel}>
      <div className="fb-dialog-card" onClick={(e) => e.stopPropagation()}>
        <div className="fb-dialog-title">{title}</div>
        <select className="fb-dialog-select" value={value} onChange={(e) => setValue(e.target.value)}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="fb-dialog-actions">
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={() => onPick(value)}>OK</Button>
        </div>
      </div>
    </div>
  );
}
